import { useEffect, useId, useMemo, useRef, useState } from "react";
import { cn } from "../utils/cn";

// ── Option type ───────────────────────────────────────────────────────────────

export interface SelectOption {
  /** Display text for the option */
  label: string;
  /** Internal value submitted on change */
  value: string;
  /** Prevents this option from being selected */
  disabled?: boolean;
  /** Options sharing the same group string are rendered under a group header */
  group?: string;
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface SelectProps {
  /** Options to render in the dropdown */
  options: SelectOption[];
  /** Currently selected value — string for single, string[] for multi */
  value: string | string[];
  /** Called with the new value when selection changes */
  onChange: (value: string | string[]) => void;
  /** Floating label above the trigger */
  label?: string;
  /** Shown when nothing is selected */
  placeholder?: string;
  /** Validation error — turns the border red */
  error?: string;
  /** Helper text below the trigger */
  helperText?: string;
  /** Allows selecting multiple values, displayed as removable pills */
  isMulti?: boolean;
  /** Enables real-time text filtering of options */
  isSearchable?: boolean;
  /** Shows a spinner in place of the chevron while data loads */
  isLoading?: boolean;
  /** Shows a clear-all button when a value is selected */
  isClearable?: boolean;
  /** Prevents interaction */
  disabled?: boolean;
  /** Controls trigger height and font size */
  size?: "sm" | "md" | "lg";
  /** Additional className on the root element */
  className?: string;
}

// ── Internal flat-list type for grouped rendering ─────────────────────────────

type FlatItem =
  | { kind: "header"; label: string }
  | { kind: "option"; option: SelectOption; idx: number };

// ── Icons ─────────────────────────────────────────────────────────────────────

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="16" height="16" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round"
      className={cn("shrink-0 transition-transform duration-150", open && "rotate-180")}
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function XIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
      aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      className="animate-spin shrink-0" aria-hidden="true">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
      <path fill="currentColor" className="opacity-75"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

// ── Size tokens ───────────────────────────────────────────────────────────────

const TRIGGER_SIZE = {
  sm: "min-h-[32px] px-2.5 py-1 text-sm gap-1",
  md: "min-h-[40px] px-3 py-2 text-base gap-1.5",
  lg: "min-h-[48px] px-4 py-2.5 text-lg gap-2",
};

const OPTION_TEXT = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-base",
};

const PILL_SIZE = {
  sm: "text-xs px-1.5 py-0.5 gap-1",
  md: "text-sm px-2 py-0.5 gap-1",
  lg: "text-sm px-2.5 py-1 gap-1.5",
};

// ── Component ─────────────────────────────────────────────────────────────────

export function Select({
  options,
  value,
  onChange,
  label,
  placeholder = "Select...",
  error,
  helperText,
  isMulti = false,
  isSearchable = false,
  isLoading = false,
  isClearable = false,
  disabled = false,
  size = "md",
  className,
}: SelectProps) {
  const id = useId();
  const listboxId = `${id}-listbox`;

  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropPos, setDropPos] = useState<"bottom" | "top">("bottom");

  // ── Value helpers ─────────────────────────────────────────────────────────

  const selectedValues = useMemo((): string[] => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  const selectedOptions = useMemo(
    () => options.filter(o => selectedValues.includes(o.value)),
    [options, selectedValues],
  );

  const singleSelected = !isMulti ? selectedOptions[0] : undefined;
  const hasValue = selectedValues.length > 0;

  // ── Filtered options and flat list ────────────────────────────────────────

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !searchQuery) return options;
    const q = searchQuery.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, isSearchable, searchQuery]);

  const flatList = useMemo((): FlatItem[] => {
    const result: FlatItem[] = [];
    const seenGroups = new Set<string>();
    let idx = 0;
    for (const opt of filteredOptions) {
      if (opt.group && !seenGroups.has(opt.group)) {
        seenGroups.add(opt.group);
        result.push({ kind: "header", label: opt.group });
      }
      result.push({ kind: "option", option: opt, idx: idx++ });
    }
    return result;
  }, [filteredOptions]);

  // ── Navigation helpers ────────────────────────────────────────────────────

  const getNextIndex = (from: number, dir: 1 | -1): number => {
    let next = from + dir;
    while (next >= 0 && next < filteredOptions.length) {
      if (!filteredOptions[next].disabled) return next;
      next += dir;
    }
    return from;
  };

  // ── Open / close ──────────────────────────────────────────────────────────

  const openDropdown = () => {
    if (disabled || isLoading) return;
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropPos(
        window.innerHeight - rect.bottom < 280 && rect.top > 280
          ? "top"
          : "bottom",
      );
    }
    setIsOpen(true);
    setSearchQuery("");
    const currentStr = Array.isArray(value) ? (value as string[])[0] : (value as string);
    const idx = filteredOptions.findIndex(o => o.value === currentStr && !o.disabled);
    setActiveIndex(idx >= 0 ? idx : -1);
    if (isSearchable) setTimeout(() => searchRef.current?.focus(), 0);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchQuery("");
    setActiveIndex(-1);
  };

  // ── Selection ─────────────────────────────────────────────────────────────

  const selectOption = (option: SelectOption) => {
    if (option.disabled) return;
    if (isMulti) {
      const next = selectedValues.includes(option.value)
        ? selectedValues.filter(v => v !== option.value)
        : [...selectedValues, option.value];
      onChange(next);
    } else {
      onChange(option.value);
      closeDropdown();
    }
  };

  const removeValue = (val: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedValues.filter(v => v !== val));
  };

  const clearAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(isMulti ? [] : "");
    closeDropdown();
  };

  // ── Keyboard ──────────────────────────────────────────────────────────────

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) { openDropdown(); return; }
        setActiveIndex(prev => getNextIndex(prev, 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        if (!isOpen) { openDropdown(); return; }
        setActiveIndex(prev =>
          getNextIndex(prev === -1 ? filteredOptions.length : prev, -1),
        );
        break;
      case "Enter":
        e.preventDefault();
        if (!isOpen) { openDropdown(); return; }
        if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
          selectOption(filteredOptions[activeIndex]);
        }
        break;
      case " ":
        if (!isSearchable) {
          e.preventDefault();
          if (!isOpen) { openDropdown(); return; }
          if (activeIndex >= 0) selectOption(filteredOptions[activeIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        closeDropdown();
        break;
      case "Home":
        if (isOpen) { e.preventDefault(); setActiveIndex(getNextIndex(-1, 1)); }
        break;
      case "End":
        if (isOpen) {
          e.preventDefault();
          setActiveIndex(getNextIndex(filteredOptions.length, -1));
        }
        break;
      default:
        break;
    }
  };

  // ── Scroll active option into view ────────────────────────────────────────

  useEffect(() => {
    if (activeIndex >= 0) {
      optionRefs.current.get(activeIndex)?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  // ── Click outside to close ────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  // ── Derived border / ring classes ─────────────────────────────────────────

  const hasError = !!error;

  const borderClass = hasError
    ? "border-red-400 dark:border-red-500"
    : isOpen
      ? "border-brand-purple dark:border-violet-500"
      : "border-border-default dark:border-border-strong";

  const ringClass =
    isOpen && !hasError
      ? "ring-2 ring-brand-purple/20 dark:ring-brand-purple/30"
      : isOpen && hasError
        ? "ring-2 ring-red-400/20 dark:ring-red-400/30"
        : "";

  const activeOptionId =
    activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div ref={containerRef} className={cn("relative flex flex-col gap-1", className)}>
      {/* Label */}
      {label && (
        <label
          htmlFor={id}
          className="text-xs font-medium text-content-secondary"
        >
          {label}
        </label>
      )}

      {/* Trigger */}
      <div
        id={id}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-activedescendant={activeOptionId}
        aria-disabled={disabled}
        aria-invalid={hasError}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        onClick={() => (isOpen ? closeDropdown() : openDropdown())}
        className={cn(
          "relative w-full flex items-center flex-wrap rounded-md border cursor-pointer",
          "bg-surface dark:bg-surface-raised",
          "outline-none transition-all duration-150",
          "focus-visible:ring-2 focus-visible:ring-brand-purple",
          "focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
          TRIGGER_SIZE[size],
          borderClass,
          ringClass,
          disabled && "bg-surface-sunken dark:bg-surface-sunken opacity-60 cursor-not-allowed",
        )}
      >
        {/* Multi-select pills */}
        {isMulti &&
          selectedOptions.map(opt => (
            <span
              key={opt.value}
              className={cn(
                "inline-flex items-center rounded-full font-medium shrink-0",
                "bg-brand-purple-light dark:bg-violet-900/40",
                "text-brand-purple dark:text-violet-400",
                PILL_SIZE[size],
              )}
            >
              {opt.label}
              {!disabled && (
                <button
                  type="button"
                  aria-label={`Remove ${opt.label}`}
                  onClick={e => removeValue(opt.value, e)}
                  className="rounded-full hover:bg-brand-purple/20 transition-colors duration-100 p-px"
                >
                  <XIcon size={10} />
                </button>
              )}
            </span>
          ))}

        {/* Search input or display text */}
        <div className="flex-1 min-w-[50px] truncate">
          {isSearchable && isOpen ? (
            <input
              ref={searchRef}
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setActiveIndex(-1);
              }}
              onClick={e => e.stopPropagation()}
              placeholder={singleSelected ? singleSelected.label : placeholder}
              className={cn(
                "w-full bg-transparent outline-none text-content-primary",
                "placeholder:text-content-tertiary",
              )}
              aria-autocomplete="list"
              aria-controls={listboxId}
            />
          ) : (
            <span
              className={cn(
                "block truncate",
                singleSelected
                  ? "text-content-primary"
                  : "text-content-tertiary",
                isMulti && hasValue ? "hidden" : "",
              )}
            >
              {singleSelected ? singleSelected.label : placeholder}
            </span>
          )}
        </div>

        {/* Right controls */}
        <div className="flex items-center gap-1 ml-auto pl-1 shrink-0 text-content-tertiary">
          {isLoading ? (
            <Spinner />
          ) : (
            <>
              {isClearable && hasValue && !disabled && (
                <button
                  type="button"
                  aria-label="Clear selection"
                  onClick={clearAll}
                  className="p-0.5 rounded hover:text-content-primary transition-colors duration-100"
                >
                  <XIcon size={14} />
                </button>
              )}
              <ChevronIcon open={isOpen} />
            </>
          )}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          id={listboxId}
          role="listbox"
          aria-multiselectable={isMulti}
          aria-label={label ?? placeholder}
          className={cn(
            "absolute left-0 right-0 z-[1000] overflow-y-auto",
            "max-h-60 rounded-card border",
            "bg-surface dark:bg-surface-raised",
            "border-border-default dark:border-border-strong",
            "animate-scale-in focus:outline-none",
            dropPos === "bottom" ? "top-full mt-1" : "bottom-full mb-1",
            label ? "" : "",
          )}
          style={{ boxShadow: "var(--shadow-dropdown)" }}
        >
          {flatList.length === 0 ? (
            <div
              className={cn(
                "px-3 py-3 text-center text-content-tertiary",
                OPTION_TEXT[size],
              )}
            >
              No options
            </div>
          ) : (
            flatList.map((item, i) => {
              if (item.kind === "header") {
                return (
                  <div
                    key={`hdr-${item.label}-${i}`}
                    className="px-3 pt-2 pb-1 text-2xs font-semibold uppercase tracking-wider text-content-tertiary select-none"
                    aria-hidden="true"
                  >
                    {item.label}
                  </div>
                );
              }

              const { option, idx } = item;
              const isSelected = selectedValues.includes(option.value);
              const isActive = activeIndex === idx;

              return (
                <div
                  key={option.value}
                  ref={el => {
                    if (el) optionRefs.current.set(idx, el);
                    else optionRefs.current.delete(idx);
                  }}
                  id={`${id}-option-${idx}`}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.disabled}
                  onMouseEnter={() => !option.disabled && setActiveIndex(idx)}
                  onClick={e => {
                    e.stopPropagation();
                    selectOption(option);
                  }}
                  className={cn(
                    "flex items-center justify-between px-3 py-2 transition-colors duration-100",
                    OPTION_TEXT[size],
                    option.disabled
                      ? "text-content-disabled cursor-not-allowed"
                      : "cursor-pointer",
                    !option.disabled && isActive
                      ? "bg-brand-purple-light dark:bg-violet-900/40"
                      : !option.disabled && isSelected
                        ? "bg-brand-purple-light/50 dark:bg-violet-900/20"
                        : "",
                    !option.disabled && isSelected
                      ? "text-brand-purple dark:text-violet-400 font-medium"
                      : !option.disabled
                        ? "text-content-primary"
                        : "",
                  )}
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected && !option.disabled && (
                    <span className="shrink-0 ml-2 text-brand-purple dark:text-violet-400">
                      <CheckIcon />
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Error / helper */}
      {(error || helperText) && (
        <p
          role={error ? "alert" : undefined}
          className={cn(
            "text-xs leading-snug",
            error ? "text-red-600 dark:text-red-400" : "text-content-secondary",
          )}
        >
          {error ?? helperText}
        </p>
      )}
    </div>
  );
}

Select.displayName = "Select";
